import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import type { CoreShellPorts } from '../../routing/domain/CoreShellPorts.ts';
import type { ShellAuthApplicationPort } from '../../app-context/domain/ports/ShellAuthApplicationPort.ts';
import type { PresentationShellPort } from '../../presentation/application/ports/PresentationShellPort.ts';
import { isPublicShellPath } from './applicationShellBootstrap.ts';
import { storeCurrentLocationAsPostLoginReturnUrl } from '../../routing/infrastructure/shellPostLoginReturnUrl.ts';
/** Sync telemetry user context. */
export async function syncTelemetryUserContext(
  auth: ShellAuthApplicationPort,
  telemetry: TelemetryPort,
  isAuth: boolean,
): Promise<void> {
  if (!isAuth) return;
  try {
    const user = await auth.getCurrentUser();
    if (user) {
      telemetry.setUserContext({ id: user.id, email: user.email, role: user.role });
    }
  } catch (e) {
    telemetry.logError('Failed to fetch user context for telemetry', { error: e });
  }
}
/** Gate unauthenticated deep link. */
export function gateUnauthenticatedDeepLink<T extends CoreShellPorts>(
  presentation: PresentationShellPort<T>,
  shellPorts: T,
  isAuth: boolean,
  currentPath: string,
): boolean {
  if (!isAuth && !isPublicShellPath(currentPath)) {
    storeCurrentLocationAsPostLoginReturnUrl();
    presentation.mountLoginInApp(shellPorts);
    globalThis.history.replaceState({}, '', '/login');
    return true;
  }
  return false;
}

/** Authenticated at `/login` → home; no session at `/login` → login shell only. */
export function gateLoginPageOnly<T extends CoreShellPorts>(
  presentation: PresentationShellPort<T>,
  shellPorts: T,
  isAuth: boolean,
  currentPath: string,
): boolean {
  if (isAuth && currentPath === '/login') {
    globalThis.history.replaceState({}, '', '/');
    return false;
  }
  if (!isAuth && currentPath === '/login') {
    presentation.mountLoginInApp(shellPorts);
    return true;
  }
  return false;
}
/** Ensure layout and main if needed. */
export async function ensureLayoutAndMainIfNeeded<T extends CoreShellPorts>(
  presentation: PresentationShellPort<T>,
  currentPath: string,
  telemetry: TelemetryPort,
  shellPorts: T,
): Promise<void> {
  if (currentPath === '/login') return;

  await initializeLayout(presentation, telemetry, shellPorts);

  let main = await presentation.getLayoutPort().getMainContentHost();
  if (!main) {
    telemetry.logError('Main element not accessible, retrying', {});
    await new Promise((resolve) => setTimeout(resolve, 200));
    main = await presentation.getLayoutPort().getMainContentHost();
    if (!main) {
      telemetry.logError('Main element still not accessible after retry', {});
    }
  }
}
/** Initializes layout. */
async function initializeLayout<T extends CoreShellPorts>(
  presentation: PresentationShellPort<T>,
  telemetry: TelemetryPort,
  shellPorts: T,
): Promise<void> {
  const app = document.getElementById('app');
  if (!app) {
    telemetry.logError('App element not found', {});
    return;
  }
  const existing = app.querySelector('layout-component') ?? document.getElementById('shell-main-content');
  if (existing) {
    await presentation.getLayoutPort().getMainContentHost();
    return;
  }
  presentation.mountLayoutRootInApp(shellPorts);
  await presentation.getLayoutPort().getMainContentHost();
}
